import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useSubscription } from "@/hooks/useSubscription";
import { toast } from "@/hooks/use-toast";
import { toast as toastSonner } from "sonner";
import { useNavigation } from "@/contexts/NavigationContext";

const C = {
  navy: "#1B3A5C", teal: "#2A9D8F", border: "#D6E4F0", bg: "#F4F8FC",
  white: "#FFFFFF", muted: "#6B85A3", danger: "#E63946", light: "#EAF2FB",
  lightTeal: "#E8F5F4",
};

const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`rounded-2xl bg-white shadow-sm border p-6 ${className}`} style={{ borderColor: C.border }}>
    {children}
  </div>
);

const Spinner = () => (
  <svg className="animate-spin h-4 w-4 inline-block mr-2" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
    <path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" className="opacity-75" />
  </svg>
);

// ─── Plan labels ───────────────────────────────────────────────────────────────
const PLAN_LABELS: Record<string, string> = {
  essentiel: "Essentiel",
  pro: "Pro",
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  active:   { label: "Actif",                   color: "#16A34A" },
  trialing: { label: "Essai gratuit actif",      color: "#2A9D8F" },
  past_due: { label: "En retard de paiement",    color: "#EA580C" },
  canceled: { label: "Résilié",                  color: "#DC2626" },
  free:     { label: "Plan Découverte",           color: "#6B85A3" },
};

export default function Profile() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, refetch: refetchProfile } = useProfile();
  const { mode, setMode } = useNavigation();
  const { isActive, isTrial, trialUsed, plan, status, daysLeftInTrial } = useSubscription();
  const proCta = trialUsed ? "Passer au Pro →" : "Passer au Pro — 14 jours gratuits →";

  // Subscription portal state
  const [portalLoading, setPortalLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Password reset
  const [pwLoading, setPwLoading] = useState(false);
  const [pwCooldown, setPwCooldown] = useState(false);

  // Fiscal modal
  const [showFiscalModal, setShowFiscalModal] = useState(false);
  const [fiscalStep, setFiscalStep] = useState(1);
  const [selectedStatut, setSelectedStatut] = useState(profile?.statut_fiscal || "micro-bnc");
  const [selectedAnciennete, setSelectedAnciennete] = useState(profile?.anciennete || "debutante");
  const [fiscalSaving, setFiscalSaving] = useState(false);

  // Delete account
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Export
  const [exportLoading, setExportLoading] = useState(false);

  const email = user?.email || "";
  const createdAt = user?.created_at ? new Date(user.created_at) : null;
  const memberSince = createdAt
    ? `Membre depuis le ${createdAt.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}`
    : "";

  const ancienneteMap: Record<string, { icon: string; label: string }> = {
    debutante: { icon: "📈", label: "Débutant·e" },
    developpement: { icon: "📊", label: "En développement" },
    etablie: { icon: "🏆", label: "Établi·e" },
  };

  const handlePasswordReset = async () => {
    if (!email) return;
    setPwLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${import.meta.env.VITE_APP_URL}/auth/callback`,
    });
    setPwLoading(false);
    if (error) {
      toast({ title: "❌ Erreur", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "✅ Email envoyé", description: "Vérifiez votre boîte mail et vos spams." });
    setPwCooldown(true);
    setTimeout(() => setPwCooldown(false), 60000);
  };

  const handleExport = async () => {
    if (!user) return;
    setExportLoading(true);
    try {
      const { data, error } = await supabase.from("recettes").select("*").eq("user_id", user.id).order("date", { ascending: true });
      if (error) throw error;
      const rows = data || [];
      const header = "Date,Montant (€),Description";
      const lines = rows.map(r =>
        `${r.date},${Number(r.montant).toFixed(2)},"${String(r.description || "").replace(/"/g, '""')}"`
      );
      const csv = [header, ...lines].join("\n");
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `psygestion-recettes-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: "✅ Export CSV téléchargé", description: `${rows.length} recettes exportées.` });
    } catch (e: any) {
      toast({ title: "❌ Erreur d'export", description: e.message, variant: "destructive" });
    }
    setExportLoading(false);
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    setDeleteLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("Session expirée, veuillez vous reconnecter.");
      const { error } = await supabase.functions.invoke("delete-account", {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) throw new Error(error.message);
      await supabase.auth.signOut();
      navigate("/");
    } catch (e: any) {
      toast({ title: "❌ Erreur", description: e.message, variant: "destructive" });
      setDeleteLoading(false);
    }
  };

  const handleSaveFiscal = async () => {
    if (!user) return;
    setFiscalSaving(true);
    const { error } = await supabase.from("profils").upsert(
      { user_id: user.id, statut_fiscal: selectedStatut, anciennete: selectedAnciennete },
      { onConflict: "user_id" }
    );
    setFiscalSaving(false);
    if (error) {
      toast({ title: "❌ Erreur", description: error.message, variant: "destructive" });
      return;
    }
    refetchProfile();
    setShowFiscalModal(false);
    setFiscalStep(1);
    toast({ title: "✅ Profil fiscal mis à jour" });
  };

  const openFiscalModal = () => {
    setSelectedStatut(profile?.statut_fiscal || "micro-bnc");
    setSelectedAnciennete(profile?.anciennete || "debutante");
    setFiscalStep(1);
    setShowFiscalModal(true);
  };

  const handleCheckout = useCallback(async () => {
    if (!user) return;
    setCheckoutLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("Session expirée, veuillez vous reconnecter.");
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          priceId: import.meta.env.VITE_STRIPE_PRICE_PRO,
          userId: user.id,
          userEmail: user.email,
          successUrl: window.location.origin + "/dashboard?subscribed=true",
          cancelUrl: window.location.origin + "/pricing",
        },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (data?.error === "already_subscribed") {
        toastSonner.warning("Vous avez déjà un abonnement Pro actif");
        navigate("/dashboard");
        return;
      }
      if (error) {
        if (import.meta.env.DEV) console.error("Checkout error:", error);
        throw new Error(error.message);
      }
      if (data?.url) {
        window.location.href = data.url;
      } else {
        if (import.meta.env.DEV) console.error("Checkout: no URL in response", data);
        throw new Error("URL de paiement introuvable");
      }
    } catch (e: any) {
      toast({ title: "❌ Erreur", description: e.message, variant: "destructive" });
      setCheckoutLoading(false);
    }
  }, [user]);

  const handlePortal = useCallback(async () => {
    if (!user) return;
    // Free users have no Stripe subscription — redirect to pricing
    if (!isActive && !isTrial && status !== "past_due") {
      navigate("/pricing");
      return;
    }
    setPortalLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error("Session expirée, veuillez vous reconnecter.");
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/customer-portal`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        },
      );
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        // If no subscription found, redirect to pricing instead of showing error
        if (data.error?.includes("abonnement") || data.error?.includes("subscription")) {
          navigate("/pricing");
          return;
        }
        throw new Error(data.error ?? "Erreur portail Stripe");
      }
    } catch (e: any) {
      // Silence "no subscription" errors for free users
      if (e.message?.includes("abonnement") || e.message?.includes("subscription")) {
        navigate("/pricing");
        return;
      }
      toast({ title: "❌ Erreur", description: e.message, variant: "destructive" });
    } finally {
      setPortalLoading(false);
    }
  }, [user, isActive, isTrial, status, navigate]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: C.bg, fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <div className="max-w-[600px] mx-auto px-4 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate("/dashboard")}
          className="text-sm mb-6 hover:underline"
          style={{ color: C.muted }}
        >
          ← Tableau de bord
        </button>

        <h1 className="text-xl font-bold mb-6" style={{ color: C.navy }}>Mon profil</h1>

        <div className="space-y-4">
          {/* ── SECTION 1 : Mon compte ── */}
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-lg">👤</span>
              <span className="font-bold text-base" style={{ color: C.navy }}>Mon compte</span>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold mb-1" style={{ color: C.muted }}>Adresse email</label>
                <input
                  type="text" readOnly value={email}
                  className="w-full rounded-xl border px-4 py-2.5 text-sm bg-gray-50 cursor-default outline-none"
                  style={{ borderColor: C.border, color: C.navy }}
                />
              </div>
              {memberSince && (
                <p className="text-xs italic" style={{ color: C.muted }}>{memberSince}</p>
              )}
              <button
                onClick={handlePasswordReset}
                disabled={pwLoading || pwCooldown}
                className="w-full py-3 rounded-xl text-sm font-semibold border-2 transition-colors disabled:opacity-50"
                style={{ borderColor: C.teal, color: C.teal, backgroundColor: C.white }}
              >
                {pwLoading && <Spinner />}
                {pwCooldown ? "Email envoyé — réessayez dans 60s" : "Envoyer un lien de changement de mot de passe"}
              </button>
            </div>
          </Card>

          {/* ── SECTION 1b : Mon abonnement ── */}
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-lg">💳</span>
              <span className="font-bold text-base" style={{ color: C.navy }}>Mon abonnement</span>
            </div>
            <div className="space-y-4">
              {/* Plan */}
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: C.muted }}>Plan actuel</span>
                <span className="font-bold text-sm" style={{ color: status === "active" ? "#16A34A" : C.navy }}>
                  {status === "trialing" || isTrial
                    ? "Pro — Période d'essai"
                    : status === "active"
                      ? "PsyGestion Pro — Actif"
                      : status === "past_due"
                        ? "Pro — Paiement en attente"
                        : "Découverte"}
                </span>
              </div>
              {/* Status */}
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: C.muted }}>Statut</span>
                <span
                  className="font-bold text-sm"
                  style={{
                    color: (status === "trialing" || isTrial) ? "#2A9D8F"
                      : status === "active" ? "#16A34A"
                      : status === "past_due" ? "#EA580C"
                      : "#6B85A3"
                  }}
                >
                  {(status === "trialing" || isTrial)
                    ? `✅ Essai gratuit actif — ${daysLeftInTrial}j restants`
                    : status === "active" ? "✅ Actif"
                    : status === "past_due" ? "⚠️ Paiement requis"
                    : status === "canceled" ? "Résilié"
                    : "Plan Découverte"}
                </span>
              </div>

              {/* ── trialing ── */}
              {(status === "trialing" || isTrial) && (
                <>
                  <p className="text-xs" style={{ color: C.muted }}>
                    Vous êtes en période d'essai gratuite. Aucun prélèvement effectué.
                  </p>
                  <button
                    onClick={handlePortal}
                    disabled={portalLoading}
                    className="w-full py-3 rounded-xl text-sm font-semibold border-2 transition-colors disabled:opacity-50"
                    style={{ borderColor: C.teal, color: C.teal, backgroundColor: C.white }}
                  >
                    {portalLoading && <Spinner />}
                    Gérer mon abonnement →
                  </button>
                </>
              )}

              {/* ── active ── */}
              {status === "active" && !isTrial && (
                <button
                  onClick={handlePortal}
                  disabled={portalLoading}
                  className="w-full py-3 rounded-xl text-sm font-semibold border-2 transition-colors disabled:opacity-50"
                  style={{ borderColor: C.teal, color: C.teal, backgroundColor: C.white }}
                >
                  {portalLoading && <Spinner />}
                  Gérer mon abonnement →
                </button>
              )}

              {/* ── past_due ── */}
              {status === "past_due" && (
                <button
                  onClick={handlePortal}
                  disabled={portalLoading}
                  className="w-full py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
                  style={{ backgroundColor: "#EA580C" }}
                >
                  {portalLoading && <Spinner />}
                  Régulariser mon paiement →
                </button>
              )}

              {/* ── canceled / null / free ── */}
              {!isActive && !isTrial && status !== "past_due" && (
                <>
                  <button
                    onClick={() => navigate("/pricing")}
                    className="w-full py-3 rounded-xl text-sm font-semibold text-white"
                    style={{ backgroundColor: C.teal }}
                  >
                    {proCta}
                  </button>
                  <p className="text-xs italic" style={{ color: C.muted, fontSize: 12 }}>
                    Accès limité — 10 recettes/mois, 3 simulations
                  </p>
                </>
              )}
            </div>
          </Card>

          {/* ── SECTION 2 : Mon profil fiscal ── */}
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-lg">📊</span>
              <span className="font-bold text-base" style={{ color: C.navy }}>Mon profil fiscal</span>
            </div>
            <div className="space-y-4">
              <div>
                <span
                  className="inline-block px-4 py-1.5 rounded-full text-sm font-bold text-white"
                  style={{ backgroundColor: profile?.statut_fiscal === "bnc-classique" ? C.navy : C.teal }}
                >
                  {profile?.statut_fiscal === "bnc-classique" ? "BNC Classique" : "Micro-BNC"}
                </span>
              </div>
              {profile?.anciennete && ancienneteMap[profile.anciennete] && (
                <div className="flex items-center gap-2 text-sm" style={{ color: C.navy }}>
                  <span>{ancienneteMap[profile.anciennete].icon}</span>
                  <span className="font-medium">{ancienneteMap[profile.anciennete].label}</span>
                </div>
              )}
              <p className="text-xs" style={{ color: C.muted }}>
                Ces informations personnalisent votre tableau de bord et vos conseils fiscaux.
              </p>
              <button
                onClick={openFiscalModal}
                className="w-full py-3 rounded-xl text-sm font-semibold border-2 transition-colors"
                style={{ borderColor: C.teal, color: C.teal, backgroundColor: C.white }}
              >
                Modifier mon profil fiscal
              </button>
            </div>
          </Card>

          {/* ── SECTION 3 : Mode d'affichage ── */}
          <Card>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg">🎛️</span>
              <span className="font-bold text-base" style={{ color: C.navy }}>Mode d'affichage</span>
            </div>
            <p className="text-xs mb-4" style={{ color: C.muted }}>Choisissez l'interface adaptée à votre pratique</p>
            <div className="grid grid-cols-2 gap-3">
              {/* Mode Essentiel */}
              <button
                onClick={async () => {
                  await setMode("debutante");
                  toast({ title: "✅ Mode mis à jour", description: "Mode Essentiel activé." });
                }}
                className="text-left rounded-xl p-4 border-2 transition-all duration-200"
                style={{
                  borderColor: mode === "debutante" ? C.teal : C.border,
                  backgroundColor: mode === "debutante" ? "#EAF7F5" : C.white,
                }}
              >
                <div className="text-2xl mb-2">🌱</div>
                <div className="font-bold text-sm mb-1" style={{ color: C.navy }}>Mode Essentiel</div>
                <div className="text-xs leading-relaxed mb-3" style={{ color: C.muted, fontSize: 13 }}>
                  Les 3 outils du quotidien — idéal pour rester focus
                </div>
                <div className="flex flex-wrap gap-1">
                  {["Tableau de bord", "Saisir", "Assistant"].map(p => (
                    <span key={p} className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: C.lightTeal, color: C.teal }}>{p}</span>
                  ))}
                </div>
              </button>

              {/* Mode Complet */}
              <button
                onClick={async () => {
                  await setMode("etablie");
                  toast({ title: "✅ Mode mis à jour", description: "Mode Complet activé." });
                }}
                className="text-left rounded-xl p-4 border-2 transition-all duration-200"
                style={{
                  borderColor: mode === "etablie" ? C.teal : C.border,
                  backgroundColor: mode === "etablie" ? "#EAF7F5" : C.white,
                }}
              >
                <div className="text-2xl mb-2">🌳</div>
                <div className="font-bold text-sm mb-1" style={{ color: C.navy }}>Mode Complet</div>
                <div className="text-xs leading-relaxed mb-3" style={{ color: C.muted, fontSize: 13 }}>
                  Tous les outils — pour optimiser chaque aspect de votre activité
                </div>
                <div className="flex flex-wrap gap-1">
                  {["Projection", "Simulateur", "+5 outils"].map(p => (
                    <span key={p} className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: C.lightTeal, color: C.teal }}>{p}</span>
                  ))}
                </div>
              </button>
            </div>
          </Card>

          {/* ── SECTION 4 : Données et confidentialité ── */}
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-lg">🔒</span>
              <span className="font-bold text-base" style={{ color: C.navy }}>Mes données et confidentialité</span>
            </div>
            <div className="space-y-4">
              <p className="text-xs" style={{ color: C.muted }}>
                Vos données sont hébergées en Europe et ne sont jamais partagées avec des tiers.
              </p>
              <button
                onClick={handleExport}
                disabled={exportLoading}
                className="w-full py-3 rounded-xl text-sm font-semibold border-2 transition-colors disabled:opacity-50"
                style={{ borderColor: C.teal, color: C.teal, backgroundColor: C.white }}
              >
                {exportLoading && <Spinner />}
                Télécharger mes recettes (CSV)
              </button>

              <div className="border-t" style={{ borderColor: C.border }} />

              <button
                onClick={() => setShowDeleteModal(true)}
                className="w-full py-3 rounded-xl text-sm font-semibold border-2 transition-colors"
                style={{ borderColor: C.danger, color: C.danger, backgroundColor: C.white }}
              >
                Supprimer définitivement mon compte
              </button>
            </div>
          </Card>

          {/* ── SECTION 5 : Informations légales ── */}
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-lg">📄</span>
              <span className="font-bold text-base" style={{ color: C.navy }}>Informations légales</span>
            </div>
            <div className="flex flex-wrap gap-4">
              <a
                href="/cgu"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium underline"
                style={{ color: C.teal }}
              >
                Conditions Générales d'Utilisation
              </a>
              <span style={{ color: C.border }}>·</span>
              <a
                href="/confidentialite"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium underline"
                style={{ color: C.teal }}
              >
                Politique de confidentialité
              </a>
              <span style={{ color: C.border }}>·</span>
              <a
                href="mailto:contact@freedomarchitect.io"
                className="text-sm font-medium underline"
                style={{ color: C.teal }}
              >
                contact@freedomarchitect.io
              </a>
            </div>
          </Card>
        </div>
      </div>

      {/* ── MODALE FISCAL ── */}
      {showFiscalModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowFiscalModal(false)}>
          <div
            className="w-full max-w-md mx-4 rounded-2xl p-6"
            style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
            onClick={e => e.stopPropagation()}
          >
            {/* Steps indicator */}
            <div className="flex items-center justify-center gap-3 mb-6">
              {[1, 2].map(s => (
                <div key={s} className="flex items-center gap-2">
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{ backgroundColor: fiscalStep >= s ? C.teal : C.border }}
                  >
                    {s}
                  </div>
                  {s === 1 && <div className="w-8 h-0.5" style={{ backgroundColor: fiscalStep >= 2 ? C.teal : C.border }} />}
                </div>
              ))}
            </div>

            {fiscalStep === 1 && (
              <>
                <h3 className="font-bold text-base mb-4 text-center" style={{ color: C.navy }}>Votre régime fiscal</h3>
                <div className="grid grid-cols-2 gap-3 mb-5">
                  {[
                    { value: "micro-bnc", label: "Micro-BNC", desc: "Réduction automatique de 34% sur vos revenus" },
                    { value: "bnc-classique", label: "BNC Classique", desc: "Charges réelles déductibles" },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setSelectedStatut(opt.value)}
                      className="rounded-xl p-4 border-2 text-center transition-all"
                      style={{
                        borderColor: selectedStatut === opt.value ? C.teal : C.border,
                        backgroundColor: selectedStatut === opt.value ? C.lightTeal : C.white,
                      }}
                    >
                      <div className="font-bold text-sm mb-1" style={{ color: C.navy }}>{opt.label}</div>
                      <div className="text-xs" style={{ color: C.muted }}>{opt.desc}</div>
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setFiscalStep(2)}
                  className="w-full py-3 rounded-xl text-white font-bold text-sm"
                  style={{ backgroundColor: C.teal }}
                >
                  Suivant
                </button>
              </>
            )}

            {fiscalStep === 2 && (
              <>
                <h3 className="font-bold text-base mb-4 text-center" style={{ color: C.navy }}>Votre ancienneté</h3>
                <div className="space-y-2 mb-5">
                  {[
                    { value: "debutante", icon: "🌱", label: "Je démarre", desc: "Moins de 2 ans" },
                    { value: "developpement", icon: "🌿", label: "En développement", desc: "2 à 5 ans" },
                    { value: "etablie", icon: "🌳", label: "Établie", desc: "Plus de 5 ans" },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setSelectedAnciennete(opt.value)}
                      className="w-full text-left rounded-xl p-3 border-2 flex items-center gap-3 transition-all"
                      style={{
                        borderColor: selectedAnciennete === opt.value ? C.teal : C.border,
                        backgroundColor: selectedAnciennete === opt.value ? C.lightTeal : C.white,
                      }}
                    >
                      <span className="text-xl">{opt.icon}</span>
                      <div>
                        <div className="font-bold text-sm" style={{ color: C.navy }}>{opt.label}</div>
                        <div className="text-xs" style={{ color: C.muted }}>{opt.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setFiscalStep(1)}
                    className="flex-1 py-3 rounded-xl text-sm font-semibold border"
                    style={{ borderColor: C.border, color: C.muted }}
                  >
                    Retour
                  </button>
                  <button
                    onClick={handleSaveFiscal}
                    disabled={fiscalSaving}
                    className="flex-1 py-3 rounded-xl text-white font-bold text-sm disabled:opacity-50"
                    style={{ backgroundColor: C.teal }}
                  >
                    {fiscalSaving && <Spinner />}
                    Enregistrer
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── MODALE SUPPRESSION ── */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowDeleteModal(false)}>
          <div
            className="w-full max-w-md mx-4 rounded-2xl p-6"
            style={{ backgroundColor: "#FFF5F5", border: `1px solid ${C.danger}40` }}
            onClick={e => e.stopPropagation()}
          >
            <div className="text-center mb-4">
              <span className="text-4xl">⚠️</span>
              <h3 className="font-bold text-lg mt-2" style={{ color: C.danger }}>Suppression définitive</h3>
            </div>
            <p className="text-sm text-center mb-6" style={{ color: C.navy }}>
              Cette action supprimera toutes vos recettes, séances et données fiscales. Elle est irréversible.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 py-3 rounded-xl text-sm font-semibold border"
                style={{ borderColor: C.border, color: C.muted, backgroundColor: C.white }}
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteLoading}
                className="flex-1 py-3 rounded-xl text-white font-bold text-sm disabled:opacity-50"
                style={{ backgroundColor: C.danger }}
              >
                {deleteLoading && <Spinner />}
                Oui, supprimer mon compte
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
